import ApartmentIcon from '@mui/icons-material/Apartment';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ChromeReaderModeOutlinedIcon from '@mui/icons-material/ChromeReaderModeOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import PaymentsIcon from '@mui/icons-material/Payments';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import TimelapseOutlinedIcon from '@mui/icons-material/TimelapseOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { Box, FormControlLabel, Grid, ListItem, ListItemText } from '@mui/material';
import { useEffect, useState } from 'react';
import { IJob, getFeeArrangementById } from 'wisestep-api';
import {
  ChipsType,
  WsColors,
  WsIconCurrency,
  WsIconEducation,
  WsIconLikeLocation,
  WsIconLocation,
  WsIconPersons,
  WsIconPosition,
  WsIconProfile,
  WsIconTag,
  WsIconText,
  WsIconTime,
  WsIconVisa,
  WsTagsList,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  ScrollableTabs,
} from 'wisestep-base';

import { useJobSettingsContext } from '../../../../../../context/jobsListContext';
import formatJobInsights from '../../../../../../utils/formatJobInsights';
import formatJobType from '../../../../../../utils/formatJobType';
import { getCategories, getJobIndustries } from '../../../../../jobs/helper';

interface jobInfoProps {
  job: IJob;
  mappedEvalutaions?: any;
  mappedInfoTemplates?: any;
}

const JobInfo = ({ job, mappedEvalutaions, mappedInfoTemplates }: jobInfoProps) => {
  const jobSettings = useJobSettingsContext();
  const jobCategories = getCategories();
  const jobIndustries = getJobIndustries();
  const taxTerms = job.taxTerms ? `On ${job.taxTerms}` : '';
  const jobCategory = jobCategories.find((object) => object.value == job.jobCategory);
  const [tabPanelPage, setTabPanelPage] = useState(0);

  const jobOccupation = jobCategory?.occupations.find(
    (occupation) => occupation.value == job.jobOccupation
  );

  const removeQuotes = (str: string): string => {
    return str.replace(/['",]+/g, '');
  };

  const getEvaluationTemplateNames = (): string[] => {
    const resp: string[] = [];
    if (mappedEvalutaions && mappedEvalutaions.length > 0) {
      mappedEvalutaions.forEach((element) => {
        resp.push(element.templateName);
      });
    }
    return resp;
  };

  const getInfoTemplatesNames = (): string[] => {
    const resp: string[] = [];
    if (mappedInfoTemplates && mappedInfoTemplates.length > 0) {
      mappedInfoTemplates.forEach((element) => {
        resp.push(element.templateName);
      });
    }
    return resp;
  };

  const jobIndustry = jobIndustries.find((industry) => industry.value == job.jobIndustryName);

  const jobInfoArray: React.ReactFragment[] = [];

  const [feeArrangement, setfeeArrangement] = useState('');

  useEffect(() => {
    const getFeeArrangement = async () => {
      if (job?.feeArrangementId) {
        const res = await getFeeArrangementById(job.feeArrangementId);
        setfeeArrangement(res.data.feeArrangementLevel);
      }
    };
    getFeeArrangement();
  }, [job.feeArrangementId]);

  jobInfoArray.push(
    <Grid item xs={6}>
      <WsIconText
        icon={<WsIconPosition color={WsColors.GreyBlack} />}
        text={String(job.numberOfPositions ?? 'Not Available')}
        popOverText="No. Of. Positions"
        textCursor="pointer"
      />
    </Grid>
  );

  jobInfoArray.push(
    <WsIconText
      icon={<WsIconCurrency color={WsColors.GreyBlack} />}
      text={
        job.salaryCurrency && job.minimumSalary && job.maximumSalary
          ? `${job.salaryCurrency} ${job.minimumSalary} - ${job.maximumSalary} ${job.salaryTimeSpan} ${taxTerms}`
          : 'Not Available'
      }
      popOverText="Salary"
      textCursor="pointer"
    />
  );

  jobInfoArray.push(
    <WsIconText
      icon={<WsIconLikeLocation color={WsColors.GreyBlack} />}
      text={job.remoteJob ? 'Remote Available' : 'Remote Unavailable'}
      popOverText="Remote Availability"
      textCursor="pointer"
    />
  );

  jobInfoArray.push(
    <WsIconText
      icon={<WsIconEducation color={WsColors.GreyBlack} />}
      text={job?.educationRequirements ? job.educationRequirements?.join(' & ') : 'Not Available'}
      popOverText="Education Requirements"
      textCursor="pointer"
    />
  );

  jobInfoArray.push(
    <WsIconText
      icon={<DnsOutlinedIcon fontSize="small" color="disabled" />}
      text={job?.experienceRequirements ? job.experienceRequirements.join(' & ') : 'Not Available'}
      popOverText="Required Experience"
      textCursor="pointer"
    />
  );
  job.workDuration &&
    job.workDurationTimeSpan &&
    jobInfoArray.push(
      <WsIconText
        icon={<TimelapseOutlinedIcon fontSize="small" color="disabled" />}
        text={`${job.workDuration} - ${job.workDurationTimeSpan}`}
        popOverText="Work Duration"
        textCursor="pointer"
      />
    );

  jobInfoArray.push(
    <WsIconText
      icon={<WsIconTime color={WsColors.GreyBlack} />}
      text={job.jobType ? formatJobType(job.jobType) : 'Not Available'}
      popOverText="Job Type"
      textCursor="pointer"
    />
  );

  job.jobStatusType &&
    jobInfoArray.push(
      <WsIconText
        icon={<ChromeReaderModeOutlinedIcon fontSize="small" color="disabled" />}
        text={job.jobStatusType}
        popOverText="Job Status Type"
        textCursor="pointer"
      />
    );

  job.workAuthorization &&
    jobInfoArray.push(
      <WsIconText
        icon={<WsIconProfile color={WsColors.GreyBlack} />}
        text={job.workAuthorization}
        popOverText="Work Authorization"
        textCursor="pointer"
      />
    );

  jobInfoArray.push(
    <WsIconText
      icon={<WsIconVisa color={WsColors.GreyBlack} />}
      text={job.visaRequirements ?? 'Not Available'}
      popOverText="Visa Requirements"
      textCursor="pointer"
    />
  );

  jobIndustry &&
    jobInfoArray.push(
      <WsIconText
        icon={<WarehouseOutlinedIcon fontSize="small" color="disabled" />}
        text={jobIndustry.label}
        popOverText="Job Industry"
        textCursor="pointer"
      />
    );

  jobCategory &&
    jobInfoArray.push(
      <WsIconText
        icon={<CategoryOutlinedIcon fontSize="small" color="disabled" />}
        text={jobCategory?.label}
        popOverText="Job Category"
        textCursor="pointer"
      />
    );

  jobOccupation &&
    jobInfoArray.push(
      <WsIconText
        icon={<WorkOutlineIcon fontSize="small" color="disabled" />}
        text={jobOccupation.label}
        popOverText="Job Occupation"
        textCursor="pointer"
      />
    );

  job.feeArrangementId &&
    feeArrangement &&
    jobInfoArray.push(
      <WsIconText
        icon={<PaymentsIcon fontSize="small" color="disabled" />}
        text={feeArrangement}
        popOverText="Fee Arrangement"
        textCursor="pointer"
      />
    );

  job.hiringFor &&
    jobInfoArray.push(
      <WsIconText
        icon={<WsIconPersons color={WsColors.GreyBlack} />}
        text={job.hiringFor}
        popOverText="Hiring For"
        textCursor="pointer"
      />
    );
  job.url &&
    jobInfoArray.push(
      <WsIconText
        icon={<LinkOutlinedIcon fontSize="small" color="disabled" />}
        text={job.url}
        popOverText="Job URL"
        textCursor="pointer"
      />
    );

  job.jobSource &&
    jobInfoArray.push(
      <WsIconText
        icon={<SourceOutlinedIcon fontSize="small" color="disabled" />}
        text={job.jobSource}
        popOverText="Job Source"
        textCursor="pointer"
      />
    );

  job.workplacePreference &&
    jobInfoArray.push(
      <WsIconText
        icon={<ApartmentIcon fontSize="small" color="disabled" />}
        text={job.workplacePreference}
        popOverText="Workplace Preference"
        textCursor="pointer"
      />
    );

  job.otherBenefitDetails &&
    jobInfoArray.push(
      <WsIconText
        icon={<WsIconTag color={WsColors.GreyBlack} />}
        text={job.otherBenefitDetails}
        popOverText="Benefits"
        textCursor="pointer"
      />
    );

  job.businessUnit &&
    jobInfoArray.push(
      <WsIconText
        icon={<BusinessCenterIcon fontSize="small" color="disabled" />}
        text={job.businessUnitName ?? ''}
        popOverText="Business Unit"
        textCursor="pointer"
      />
    );

  job.clientBillRate &&
    jobInfoArray.push(
      <WsIconText
        icon={<WsIconCurrency color={WsColors.GreyBlack} />}
        text={`${job.clientBillRateCurrency ?? ''} ${job.clientBillRate ?? ''} ${
          job.clientBillRateTimeSpan ?? ''
        }`}
        popOverText="Client Bill Rate"
        textCursor="pointer"
      />
    );

  const InfoLayout = () => {
    return (
      <>
        {jobInfoArray.map((item, index) => (
          <Grid item xs={4} key={index} pt={2}>
            {item}
          </Grid>
        ))}
      </>
    );
  };

  return (
    <Box m={2}>
      <Grid container spacing={2} flex-direction="column" pb={2}>
        <InfoLayout />
      </Grid>
      {job.locations && jobSettings.locations?.hide === false && (
        <>
          <FormControlLabel
            control={
              <Box pr={1}>
                <WsIconLocation />
              </Box>
            }
            label={
              <WsTypography
                variant={WsTypoVariants.SUB_TITLE}
                color={WsTypoColors.PRIMARY}
                cursor="pointer"
              >
                Location :
              </WsTypography>
            }
          />
          <Box py={1} px={2}>
            <WsTagsList
              chipArray={job.locations.map((location) =>
                [location.city, location.state, location.country].filter(Boolean).join(',')
              )}
              count={3}
              type={ChipsType.SECONDARY}
            />
          </Box>
        </>
      )}
      {job.skills && jobSettings.skills?.hide === false && (
        <>
          <FormControlLabel
            control={
              <Box pr={1}>
                <WsIconTag />
              </Box>
            }
            label={
              <WsTypography
                variant={WsTypoVariants.SUB_TITLE}
                color={WsTypoColors.PRIMARY}
                cursor="pointer"
              >
                Skills :
              </WsTypography>
            }
          />
          <Box py={1} px={2}>
            <WsTagsList chipArray={job.skills} count={5} type={ChipsType.PRIMARY} />
          </Box>
        </>
      )}
      <FormControlLabel
        control={
          <Box pr={1}>
            <WsIconTag />
          </Box>
        }
        label={
          <WsTypography
            variant={WsTypoVariants.SUB_TITLE}
            color={WsTypoColors.PRIMARY}
            cursor="pointer"
          >
            Evaluation Templates :
          </WsTypography>
        }
      />
      <Box py={1} px={2}>
        <WsTagsList chipArray={getEvaluationTemplateNames()} count={5} type={ChipsType.PRIMARY} />
      </Box>
      <FormControlLabel
        control={
          <Box pr={1}>
            <WsIconTag />
          </Box>
        }
        label={
          <WsTypography
            variant={WsTypoVariants.SUB_TITLE}
            color={WsTypoColors.PRIMARY}
            cursor="pointer"
          >
            Information Templates :
          </WsTypography>
        }
      />
      <Box py={1} px={2}>
        <WsTagsList chipArray={getInfoTemplatesNames()} count={5} type={ChipsType.PRIMARY} />
      </Box>
      {job.tags && jobSettings.tags?.hide === false && (
        <>
          <FormControlLabel
            control={
              <Box pr={1}>
                <WsIconTag />
              </Box>
            }
            label={
              <WsTypography
                variant={WsTypoVariants.SUB_TITLE}
                color={WsTypoColors.PRIMARY}
                cursor="pointer"
              >
                Tags :
              </WsTypography>
            }
          />
          <Box py={1} px={2} mb={1}>
            <WsTagsList
              chipArray={job.tags}
              count={5}
              type={ChipsType.PRIMARY}
              allowDelete={true}
            />
          </Box>
        </>
      )}
      {job.description && job.questions == null && jobSettings.description?.hide === false && (
        <>
          <WsTypography
            variant={WsTypoVariants.SUB_TITLE}
            color={WsTypoColors.PRIMARY}
            cursor="pointer"
          >
            Job Description
          </WsTypography>
          <div dangerouslySetInnerHTML={{ __html: job.description }}></div>
        </>
      )}

      {job.description && job.questions && jobSettings.description?.hide === false && (
        <>
          <ScrollableTabs
            scrollList={[
              { tabName: 'Job Description' },
              {
                tabName: 'Interview Questions',
              },
            ]}
            getTabIndex={setTabPanelPage}
            defaultValue={tabPanelPage}
          />
          {tabPanelPage === 0 && (
            <Grid container sx={{ display: 'flex' }} mt={3} ml={5}>
              <WsTypography
                variant={WsTypoVariants.SUB_TITLE}
                color={WsTypoColors.PRIMARY}
                cursor="pointer"
              >
                Job Description
              </WsTypography>
              <div dangerouslySetInnerHTML={{ __html: job.description }}></div>
            </Grid>
          )}
          {tabPanelPage === 1 && job.questions && (
            <>
              <Grid container p={3} spacing={2} direction="column">
                <Grid item xs={6} justify-content="center">
                  <WsTypography variant={WsTypoVariants.TITLE}>
                    Interview questions for the Role of {job.title}
                  </WsTypography>
                </Grid>
                <Grid item xs={3}>
                  {job.questions.map((question, index) => (
                    <ListItem style={{ marginTop: '10px' }} key={index}>
                      <ListItemText
                        style={{ marginLeft: '20px' }}
                        primary={removeQuotes(question)}
                      />
                    </ListItem>
                  ))}
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}

      {job.insights && job.insights.length > 0 && jobSettings.insight?.hide === false && (
        <>
          <WsTypography
            variant={WsTypoVariants.SUB_TITLE}
            color={WsTypoColors.PRIMARY}
            cursor="pointer"
          >
            Job Insights
          </WsTypography>
          <Box p={1}>
            {formatJobInsights(job.insights).map((item, index) => (
              <Box key={index}>
                <div dangerouslySetInnerHTML={{ __html: item }}></div>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default JobInfo;
